import { Data, Effect } from 'effect';
import type * as PDFJSLib from 'pdfjs-dist';

import type { PdfJsLib } from './types';

// ── Redacted failure channel ─────────────────────────────────────────────────
// The typed error carries a static operation label only — never the pdfjs-dist
// driver message, worker URL, file bytes, or download name. The original cause
// rides a side WeakMap so the Promise adapters reject with the exact error the
// underlying browser/pdfjs API produced.

export type PdfJsOperation = 'load' | 'download';

export class PdfJsError extends Data.TaggedError('PdfJsError')<{
  readonly operation: PdfJsOperation;
}> {}

const pdfJsErrorCauses = new WeakMap<PdfJsError, unknown>();

function pdfJsFailure(operation: PdfJsOperation, cause: unknown): PdfJsError {
  const error = new PdfJsError({ operation });
  pdfJsErrorCauses.set(error, cause);
  return error;
}

export function pdfJsErrorCause(error: PdfJsError): unknown {
  return pdfJsErrorCauses.get(error) ?? error;
}

/** Restore the original cause so legacy callers observe the same rejection. */
export function restorePdfJsError<A>(
  program: Effect.Effect<A, PdfJsError>,
): Effect.Effect<A, unknown> {
  return program.pipe(Effect.catch((error) => Effect.fail(pdfJsErrorCauses.get(error) ?? error)));
}

let cached: Promise<typeof PDFJSLib> | null = null;

// pdfjs-dist 5.x uses the TC39 Map.prototype.getOrInsert{,Computed} proposal,
// which ships only in very recent browsers (Chrome 138+, Safari 26+). On older
// mobile browsers the call throws "getOrInsertComputed is not a function" the
// moment a PDF is parsed. Install the polyfill once, before any pdfjs code runs.
function installMapUpsertPolyfill() {
  const proto = Map.prototype as unknown as {
    getOrInsert?: <K, V>(key: K, value: V) => V;
    getOrInsertComputed?: <K, V>(key: K, callbackfn: (key: K) => V) => V;
  };
  if (typeof proto.getOrInsertComputed !== 'function') {
    Object.defineProperty(Map.prototype, 'getOrInsertComputed', {
      configurable: true,
      writable: true,
      value: function getOrInsertComputed<K, V>(
        this: Map<K, V>,
        key: K,
        callbackfn: (key: K) => V,
      ): V {
        if (this.has(key)) return this.get(key) as V;
        const value = callbackfn(key);
        this.set(key, value);
        return value;
      },
    });
  }
  if (typeof proto.getOrInsert !== 'function') {
    Object.defineProperty(Map.prototype, 'getOrInsert', {
      configurable: true,
      writable: true,
      value: function getOrInsert<K, V>(this: Map<K, V>, key: K, value: V): V {
        if (this.has(key)) return this.get(key) as V;
        this.set(key, value);
        return value;
      },
    });
  }
}

/** Native loader program: polyfill install → dynamic import → worker wiring. */
export const loadPdfjsEffect = Effect.fn('pdfjs.load')(function* (): Effect.fn.Return<
  typeof PDFJSLib,
  PdfJsError
> {
  yield* Effect.try({
    try: () => installMapUpsertPolyfill(),
    catch: (cause) => pdfJsFailure('load', cause),
  });
  const lib = yield* Effect.tryPromise({
    try: () => import('pdfjs-dist'),
    catch: (cause) => pdfJsFailure('load', cause),
  });
  // Use a polyfilled worker entry rather than the raw pdf.worker.min.mjs so
  // older mobile browsers don't crash on Map.prototype.getOrInsertComputed.
  return yield* bindPdfjsWorkerEffect(
    lib,
    () => new Worker(new URL('./pdfjs.worker.ts', import.meta.url), { type: 'module' }),
  );
});

/** Every document borrows the runtime worker; closing one cannot destroy another's loader. */
export const bindPdfjsWorkerEffect = Effect.fn('pdfjs.bindWorker')(function* (
  lib: typeof PDFJSLib,
  createWorker: () => Worker,
) {
  let adopted = false;
  let shared: PDFJSLib.PDFWorker | undefined;
  const port = yield* Effect.acquireRelease(
    Effect.try({ try: createWorker, catch: (cause) => pdfJsFailure('load', cause) }),
    (port) =>
      adopted
        ? Effect.void
        : Effect.try({
            try: () => {
              try {
                shared?.destroy();
              } finally {
                port.terminate();
              }
            },
            catch: (cause) => pdfJsFailure('load', cause),
          }).pipe(Effect.ignore),
  );
  shared = yield* Effect.try({
    try: () => lib.PDFWorker.create({ port }),
    catch: (cause) => pdfJsFailure('load', cause),
  });
  yield* Effect.tryPromise({
    try: () => shared!.promise,
    catch: (cause) => pdfJsFailure('load', cause),
  });
  const worker = shared;
  const getDocument: typeof lib.getDocument = (source) => {
    const options =
      typeof source === 'string' || source instanceof URL
        ? { url: source }
        : source instanceof ArrayBuffer || ArrayBuffer.isView(source)
          ? { data: source }
          : (source ?? {});
    return lib.getDocument({
      ...options,
      worker: 'worker' in options ? (options.worker ?? worker) : worker,
    });
  };
  const runtime = { ...lib, getDocument };
  adopted = true;
  return runtime;
}, Effect.scoped);

function loadPdfjs(): Promise<typeof PDFJSLib> {
  return Effect.runPromise(restorePdfJsError(loadPdfjsEffect()));
}

export function getPdfjs(): Promise<typeof PDFJSLib> {
  // Memoize the in-flight promise (not just the resolved lib) so overlapping
  // first calls share one load and one Worker instead of each spawning their
  // own and clobbering GlobalWorkerOptions.workerPort.
  cached ??= loadPdfjs().catch((error: unknown) => {
    cached = null; // let a failed load retry
    throw error;
  });
  return cached;
}

/** Minimal playground-facing adapter backed by the bundled pdfjs-dist build. */
export async function getPdfJsLib(): Promise<PdfJsLib> {
  return (await getPdfjs()) as unknown as PdfJsLib;
}

function waitForDownloadDispatch(): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, 0);
  });
}

export function uint8ArrayToBlobPart(bytes: Uint8Array): BlobPart {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer as ArrayBuffer;
}

export function uint8ArrayToBlob(bytes: Uint8Array, type = 'application/pdf'): Blob {
  return new Blob([uint8ArrayToBlobPart(bytes)], { type });
}

/** Native download-dispatch program; the DOM side effects run synchronously. */
export const downloadBlobEffect = Effect.fn('pdfjs.downloadBlob')(function* (
  bytes: Uint8Array | Blob,
  name: string,
  type = 'application/pdf',
): Effect.fn.Return<void, PdfJsError> {
  yield* Effect.try({
    try: () => {
      const blob = bytes instanceof Blob ? bytes : uint8ArrayToBlob(bytes, type);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.rel = 'noopener';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
    },
    catch: (cause) => pdfJsFailure('download', cause),
  });
  // Never rejects — the dispatch is already done; this just yields a tick.
  yield* Effect.promise(() => waitForDownloadDispatch());
});

export function downloadBlob(
  bytes: Uint8Array | Blob,
  name: string,
  type = 'application/pdf',
): Promise<void> {
  return Effect.runPromise(restorePdfJsError(downloadBlobEffect(bytes, name, type)));
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}
